import {
  LOCAL_DATE_TIME_PATTERN,
  MIN_ONE_TIME_SCHEDULE_AHEAD_MS,
  ONE_TIME_SCHEDULE_INVALID_ERROR,
  TIME_OF_DAY_PATTERN,
  TIME_OF_DAY_SCHEDULE_PRESETS,
} from "../constants";
import { cronScheduleSchema } from "../schema/cron-schedule";
import type { ICronJob, TCronSchedule } from "../types";
import type {
  IIsOneTimeScheduleTooSoonProps,
  IResolveScheduleDisplayTimezoneProps,
  IResolveSchedulePayloadTimezoneProps,
  TOneTimeScheduleIssue,
  TOneTimeScheduleIssueKey,
} from "./schedule.types";

// NOTE: True when a one-shot ("at") agent has nothing left to do, so the UI shows
// it as "Completed" (counted in the Completed chip, Pause/Resume hidden) rather
// than "Paused".
//
// A one-shot is spent either because it actually ran — the runner disables it
// after execution regardless of what fired it — or because its scheduled moment
// has passed, which it can no longer be resumed for. The `lastRunAt` half matters
// for "Run Now" (NSAI-4212): triggering a one-shot manually before its scheduled
// time disables the job while `schedule.at` is still in the future, so a
// wall-clock test alone read that as merely paused.
//
// Still `false` for a one-shot the user paused before it ran and whose time is
// yet to come — that one is genuinely resumable and must keep its toggle.
/**
 * NOTE: NSAI-6123 — `agent.schedule` is the jsonb column as stored, and until
 * NSAI-6123 the API accepted any object for it. Both clients narrow on `kind`
 * and then read `expr` / `at` / `everyMs`, so a row with an unknown kind threw
 * inside the list render. This is the positive test to run before narrowing;
 * a schedule that fails it renders a fallback label instead of taking the
 * screen down. The type is a claim about the data — this is the check.
 */
export const isKnownCronSchedule = (value: unknown): value is TCronSchedule =>
  cronScheduleSchema.safeParse(value).success;

export const isExpiredOneTimeAgent = (agent: ICronJob): boolean =>
  !agent.enabled &&
  agent.schedule.kind === "at" &&
  (Boolean(agent.lastRunAt) || new Date(agent.schedule.at) <= new Date());

/**
 * NOTE: NSAI-5591 — the IANA zone a cron schedule's wall-clock time was picked
 * in, sent with the schedule so the server evaluates "08:00" in the user's zone
 * instead of its own. Shared by web and mobile so the two clients cannot drift.
 *
 * Falls back to "UTC", which is what the server already assumes for a schedule
 * that carries no zone — so a browser that cannot report one behaves exactly as
 * it does today rather than guessing.
 */
export const getClientTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// NOTE: Spelled out so `resolveSchedulePayloadTimezone` reads as returning a
// value on every branch — an omitted zone is a deliberate outcome there, not a
// fall-through.
const NO_SCHEDULE_TIMEZONE = undefined;

// NOTE: Field-for-field comparable form of a cron expression — trimmed, with runs
// of internal whitespace collapsed to one space. Deliberately does NOT normalize
// field *values* (names to numbers, etc.); that belongs to the server's
// `normalizeCronExpression`, and treating "MON-FRI" as unchanged versus "1-5"
// here would be a guess about intent.
const toComparableCronExpression = (expr: string): string =>
  expr.trim().split(/\s+/).join(" ");

/**
 * NOTE: Review follow-up — reads a timezone off a schedule WITHOUT trusting its
 * declared type. `tz` is typed `string | undefined`, but it arrives over the wire
 * out of a jsonb column, so a legacy or hand-edited row can hold a number or an
 * object. `tz?.trim()` guards null and undefined but NOT a wrong type —
 * `(123)?.trim()` throws — and this runs inside a React render, so it would blank
 * the agents list rather than degrade. Anything unusable reads as "no zone",
 * matching the server's `resolveTimezone`.
 */
const readScheduleTimezone = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

// NOTE: Review follow-up — the same validity test the server applies before it
// hands a zone to the scheduler, so the label can never name a zone the job does
// not actually run in.
const isResolvableTimezone = (timezone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * NOTE: NSAI-5591 — decides which IANA zone a cron schedule is saved with. The
 * four cases are deliberate, and three of them exist to guarantee that this fix
 * cannot re-time a schedule the user did not touch:
 *
 * 1. Not a cron schedule -> no zone. "every"/"at" carry no wall clock ("at" is
 *    already an absolute UTC instant), so a zone would be meaningless.
 * 2. The job already has a zone -> keep it, always. An admin (or a colleague in
 *    another country) editing someone else's agent sees the raw hour and must not
 *    silently move the job into their own zone.
 * 3. A legacy zone-less job whose expression is unchanged -> still no zone. Saving
 *    an edit to the name or prompt must not move when the job fires. These rows
 *    keep firing exactly as they do today until someone edits the schedule itself.
 * 4. Anything else — a new job, or a zone-less job whose schedule was just
 *    changed -> the client's zone, because the user authored that wall clock here
 *    and now.
 */
export const resolveSchedulePayloadTimezone = (
  props: IResolveSchedulePayloadTimezoneProps,
): string | undefined => {
  const { nextSchedule, clientTimezone } = props;
  // NOTE: NSAI-6123 review follow-up — the stored row is read here too, and a
  // malformed one (`{ kind: "cron" }` with no `expr`) reached `.trim()` below and
  // threw from the submit handler: the form opened on the defaults as intended,
  // but saving a cron preset — the very repair the fallback exists for — aborted
  // before the request. A row the guard rejects is treated as no current
  // schedule, which is case 4 below: the user's own zone, because the user
  // authored this wall clock here and now.
  const currentSchedule =
    props.currentSchedule && isKnownCronSchedule(props.currentSchedule)
      ? props.currentSchedule
      : undefined;

  if (nextSchedule.kind !== "cron") {
    return NO_SCHEDULE_TIMEZONE;
  }

  // NOTE: Copilot review follow-up — only a stored zone that actually RESOLVES is
  // preserved. Keeping an unresolvable one locked the agent out of editing: the form
  // resent the bad zone, the new server guard answered 400, and changing the
  // schedule resent it too, so there was no way to correct the row from either
  // client. Such a row is plausible because the chat tool used to pass an
  // LLM-supplied zone through with no validation at all.
  //
  // Treating it as zone-less instead lets the row heal on the next save — to UTC if
  // only the name changed (which is what it was already executing as), or to the
  // user's own zone if they touched the schedule.
  const storedTimezoneValue =
    currentSchedule?.kind === "cron"
      ? readScheduleTimezone(currentSchedule.tz)
      : "";
  const storedTimezone =
    storedTimezoneValue && isResolvableTimezone(storedTimezoneValue)
      ? storedTimezoneValue
      : "";
  if (storedTimezone) {
    return storedTimezone;
  }

  // NOTE: Review follow-up — compared with internal whitespace collapsed, so an
  // equivalent expression that merely re-spaced ("0  8 * * 1-5") is not mistaken
  // for a changed schedule and does not pull a legacy row into the client's zone.
  const isUnchangedLegacySchedule =
    currentSchedule?.kind === "cron" &&
    toComparableCronExpression(currentSchedule.expr) ===
      toComparableCronExpression(nextSchedule.expr);

  return isUnchangedLegacySchedule ? NO_SCHEDULE_TIMEZONE : clientTimezone;
};

/**
 * NOTE: NSAI-5591 — the zone to name beside a schedule, or undefined when naming
 * it would only add noise.
 *
 * The panel used to render "Weekdays at 08:00" straight off the cron fields while
 * rendering "Next run" as an instant in the viewer's zone — two different frames,
 * which is how a Dubai customer saw a schedule of 08:00 sitting next to a next run
 * of 12:00 PM with nothing to explain the gap. Naming the zone whenever it differs
 * from the viewer's closes that gap: a zone-less legacy row now reads
 * "Weekdays at 08:00 (UTC)", which is the truth about when it fires.
 *
 * Returns undefined — so the label is byte-identical to before — when the schedule
 * has no wall clock, or when its zone is the viewer's own. That last case is the
 * common one after this fix, which is why the display does not change for the
 * people who were never affected.
 */
export const resolveScheduleDisplayTimezone = (
  props: IResolveScheduleDisplayTimezoneProps,
): string | undefined => {
  const { schedule, viewerTimezone } = props;

  if (schedule.kind !== "cron") {
    return NO_SCHEDULE_TIMEZONE;
  }

  // NOTE: Mirrors the server's `resolveTimezone` in full — a schedule with no zone
  // is evaluated as UTC, and so is one whose stored zone the runtime cannot
  // resolve. Naming that unusable zone would tell the reader the job runs somewhere
  // it does not, which is the very class of mismatch this ticket is about.
  const storedTimezone = readScheduleTimezone(schedule.tz);
  const effectiveTimezone =
    storedTimezone && isResolvableTimezone(storedTimezone)
      ? storedTimezone
      : "UTC";

  return effectiveTimezone === readScheduleTimezone(viewerTimezone)
    ? NO_SCHEDULE_TIMEZONE
    : effectiveTimezone;
};

/**
 * NOTE: NSAI-4795 — true only for a wall clock the friendly presets can actually
 * compile. Both clients turn this field into cron with `split(":")` + `Number()`,
 * which is happy to read "09:0099" as hour 9, minute 99 and emit `99 9 * * *` —
 * a five-field expression that is well-formed but out of range. That reached the
 * server, whose cron validator answered with its own message ("Use 5 fields:
 * minute (0-59) ... Supported: * , - / L #") to a user who had picked their
 * schedule from a dropdown and never saw a cron field.
 *
 * So the range lives here, in front of the compile, and is shared: web is only
 * shielded by `<input type="time">` refusing the keystrokes, which is the control
 * protecting it rather than the schema.
 */
export const isValidTimeOfDayValue = (value: string): boolean =>
  TIME_OF_DAY_PATTERN.test(value.trim());

// NOTE: NSAI-4795 — whether a preset's cron expression is compiled from the Time
// field, and therefore whether that field is on screen and worth validating. The
// other presets keep a stale `time` in form state while hidden, so validating it
// unconditionally would block a save on a field the user cannot see.
export const isTimeOfDaySchedulePreset = (preset: string): boolean =>
  TIME_OF_DAY_SCHEDULE_PRESETS.some((candidate) => candidate === preset);

export const toLocalDateTimeValue = (date: Date): string => {
  const pad = (part: number): string => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// NOTE: Returns null rather than an Invalid Date so callers cannot hand an
// unusable value to `toISOString()`, which throws instead of returning NaN.
export const parseLocalDateTimeValue = (value: string): Date | null => {
  const trimmedValue = value.trim();
  if (!LOCAL_DATE_TIME_PATTERN.test(trimmedValue)) {
    return null;
  }
  const parsed = new Date(trimmedValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  // NOTE: A wall clock inside a spring-forward gap does not exist, and `Date`
  // silently moves it to the far side of the jump. Storing that would run the
  // agent at an hour the user never chose, so it is rejected instead.
  return toLocalDateTimeValue(parsed) === trimmedValue ? parsed : null;
};

// NOTE: Why a reason and not just a boolean: a value two minutes out is in the
// future yet still rejected, so answering "must be in the future" told the user
// something they could see was untrue and left the real rule — the lead time the
// runner needs — unsaid. The three cases are distinguished here, once, so every
// client words them the same way.
export const getOneTimeScheduleIssue = ({
  value,
  nowMs,
}: IIsOneTimeScheduleTooSoonProps): TOneTimeScheduleIssue | null => {
  const parsed = parseLocalDateTimeValue(value);
  if (!parsed) {
    return "invalid";
  }
  const floorMs = nowMs ?? Date.now();
  if (parsed.getTime() <= floorMs) {
    return "past";
  }
  return parsed.getTime() < floorMs + MIN_ONE_TIME_SCHEDULE_AHEAD_MS
    ? "tooSoon"
    : null;
};

export const isOneTimeScheduleTooSoon = (
  props: IIsOneTimeScheduleTooSoonProps,
): boolean => getOneTimeScheduleIssue(props) !== null;

// NOTE: The one place an issue becomes copy, so the three "Once at" forms
// (mobile Agents, web Agents, admin cron jobs) cannot word the same rejection
// differently. `invalid` reuses the in-past string: a wall clock that does not
// exist is not worth its own sentence, and pointing the user at a fresh pick is
// the same remedy. The `tooSoon` string interpolates `minutes`, which callers
// pass as MIN_ONE_TIME_SCHEDULE_AHEAD_MINUTES.
export const getOneTimeScheduleIssueKey = (
  issue: TOneTimeScheduleIssue,
): TOneTimeScheduleIssueKey =>
  issue === "tooSoon"
    ? "form.validation.dateTimeTooSoon"
    : "form.validation.dateTimeInPast";

// NOTE: NSAI-4692 — the one place a "Once at" form value becomes the UTC instant
// the API stores. `new Date("").toISOString()` throws `RangeError: Date value
// out of bounds`, which on mobile killed the submit handler silently. Both
// clients call this, so an unusable value fails as a named error instead.
export const toOneTimeScheduleIso = (value: string): string => {
  const parsed = parseLocalDateTimeValue(value);
  if (!parsed) {
    throw new Error(ONE_TIME_SCHEDULE_INVALID_ERROR);
  }
  return parsed.toISOString();
};
